import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import { currentLocale } from '../../i18n';
import useCanSuggestMap from '../../hooks/useCanSuggestMap';
import MapImage from '../common/MapImage';
import './SuggestMap.css';

// Pool-category labels resolved via t() at render (module constant can't call
// hooks). The technical pool names (Active/Reserve Pool, Classic) stay English
// in every language; community/unknown reuse the news category keys.
const CATEGORY_LABEL = {
  active: 'maps.poolActive',
  reserve: 'maps.poolReserve',
  classic: 'maps.poolClassic',
  community: 'news.catCommunity',
  unknown: 'news.catOther',
};

const CATEGORY_ORDER = ['active', 'reserve', 'classic', 'community', 'unknown'];

// Ineligibility reasons → translation keys (resolved with the min-match count).
const REASON_KEY = {
  auth_required: 'maps.reasonAuth',
  no_player: 'maps.reasonNoPlayer',
  no_steam: 'maps.reasonNoSteam',
  not_enough_matches: 'maps.reasonNotEnough',
};

/**
 * Player-facing form for submitting a community map tip. Server enforces
 * eligibility on POST, but the UI also calls ``can-suggest`` up-front so
 * ineligible users see a clear explanation instead of a generic 403.
 */
export default function SuggestMap() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { eligible, reason, minMatches, loading } = useCanSuggestMap();

  const [tag, setTag] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState(null);
  const [activeMaps, setActiveMaps] = useState([]);
  const [mapsExpanded, setMapsExpanded] = useState(false);
  const [modeFilter, setModeFilter] = useState('competitive');

  useEffect(() => {
    fetch(`${API_URL}/api/maps`)
      .then((r) => r.json())
      .then((d) => {
        const maps = Object.entries(d.maps || {})
          .filter(([, info]) => info.active !== false)
          .map(([tagKey, info]) => {
            const gameModes = Array.isArray(info.game_modes) && info.game_modes.length > 0
              ? info.game_modes
              : ['competitive'];
            return {
              tag: tagKey,
              name: info.name || tagKey,
              category: info.category || 'unknown',
              image_url: info.image_url,
              game_modes: gameModes,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, currentLocale()));
        setActiveMaps(maps);
      })
      .catch(() => setActiveMaps([]));
  }, []);

  if (loading) {
    return <div className="suggest-map-page"><div className="loading-screen"><div className="loader" /></div></div>;
  }

  if (!eligible) {
    const copy = t(REASON_KEY[reason] || REASON_KEY.not_enough_matches, { count: minMatches });
    return (
      <div className="suggest-map-page">
        <h1 className="page-title">{t('maps.suggestTitle')}</h1>
        <div className="suggest-map-gate">
          <p>{copy}</p>
          <button type="button" className="suggest-map-btn" onClick={() => navigate('/stats/maps')}>
            {t('maps.backToMaps')}
          </button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!tag.trim()) {
      setErr(t('maps.tagRequired'));
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const resp = await fetch(`${API_URL}/api/maps/suggestions`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tag: tag.trim(),
          name: name.trim(),
          notes: notes.trim(),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setErr(data.error || data.detail || t('maps.errorWithStatus', { status: resp.status }));
        setBusy(false);
        return;
      }
      setSuccess(data);
    } catch (ex) {
      setErr(ex.message);
    }
    setBusy(false);
  };

  if (success) {
    return (
      <div className="suggest-map-page">
        <h1 className="page-title">{t('maps.thanks')}</h1>
        <div className="suggest-map-success">
          <p>{t('maps.tipSaved', { name: success.name || success.tag })}</p>
          {success.preview_image_url && (
            <img src={success.preview_image_url} alt={success.name} className="suggest-map-preview" />
          )}
          <div className="suggest-map-actions">
            <button type="button" className="suggest-map-btn" onClick={() => {
              setSuccess(null);
              setTag('');
              setName('');
              setNotes('');
            }}>
              {t('maps.suggestAnother')}
            </button>
            <button type="button" className="suggest-map-btn primary" onClick={() => navigate('/stats/maps')}>
              {t('maps.backToMaps')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const visibleMaps = modeFilter === 'all' 
    ? activeMaps 
    : activeMaps.filter((m) => m.game_modes.includes(modeFilter));

  const grouped = visibleMaps.reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});
  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]?.length),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <div className="suggest-map-page">
      <h1 className="page-title">{t('maps.suggestTitle')}</h1>
      <p className="suggest-map-intro">{t('maps.suggestIntro')}</p>

      {activeMaps.length > 0 && (
        <section className="suggest-map-pool">
          <button
            type="button"
            className="suggest-map-pool-toggle"
            onClick={() => setMapsExpanded((v) => !v)}
            aria-expanded={mapsExpanded}
          >
            <span>
              {t('maps.seePool', { count: activeMaps.length })}
            </span>
            <span className="suggest-map-pool-arrow">{mapsExpanded ? '▲' : '▼'}</span>
          </button>
          {mapsExpanded && (
            <div className="suggest-map-pool-body">
              <p className="suggest-map-pool-hint">
                {t('maps.poolHint')}
              </p>
              <div className="suggest-map-mode-filter">
                {[
                  ['competitive', '5v5'],
                  ['wingman', '2v2'],
                  ['duel', '1v1'],
                  ['all', 'Alla'],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    className={`suggest-map-btn ${modeFilter === val ? 'primary' : ''}`}
                    onClick={() => setModeFilter(val)}
                  >
                    {val === 'all' ? t('matches.all') : label}
                  </button>
                ))}
              </div>
              {orderedCategories.length === 0 ? (
                <p className="suggest-map-pool-empty">{t('maps.noMapsForMode')}</p>
              ) : (
                orderedCategories.map((cat) => (
                  <div key={cat} className="suggest-map-pool-group">
                    <h3 className="suggest-map-pool-category">{CATEGORY_LABEL[cat] ? t(CATEGORY_LABEL[cat]) : cat}</h3>
                    <div className="suggest-map-pool-grid">
                      {grouped[cat].map((m) => (
                        <div key={m.tag} className="suggest-map-pool-card">
                          <div className="suggest-map-pool-image">
                            <MapImage imageUrl={m.image_url} mapName={m.name} />
                          </div>
                          <div className="suggest-map-pool-name">{m.name}</div>
                          <div className="suggest-map-pool-tag">{m.tag}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      )}

      <form className="suggest-map-form" onSubmit={submit}>
        <label className="suggest-map-field">
          <span>{t('maps.workshopLinkLabel')}</span>
          <input
            type="text"
            placeholder="https://steamcommunity.com/sharedfiles/filedetails/?id=..."
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            required
          />
          <small>{t('maps.workshopIdAuto')}</small>
        </label>
        <label className="suggest-map-field">
          <span>{t('maps.displayNameOptional')}</span>
          <input
            type="text"
            placeholder={t('maps.displayNamePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="suggest-map-field">
          <span>{t('maps.adminComment')}</span>
          <textarea
            rows={4}
            placeholder={t('maps.adminCommentPlaceholder')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
          />
        </label>
        {err && <div className="suggest-map-error">{err}</div>}
        <div className="suggest-map-actions">
          <button type="button" className="suggest-map-btn" onClick={() => navigate('/stats/maps')} disabled={busy}>
            {t('matchDetail.cancel')}
          </button>
          <button type="submit" className="suggest-map-btn primary" disabled={busy}>
            {busy ? t('maps.sending') : t('maps.sendTip')}
          </button>
        </div>
      </form>
    </div>
  );
}
